import React from 'react';
import { Provider } from 'react-redux';
import { createStore } from 'redux';
import { uniq } from 'lodash';
import { LOCAL_STORAGE_KEYS } from 'utils/constants/localStorageKeys';
import { MOBILE_WIDTH } from 'new-ui/constants';
import setJoinedAt from 'functions/setJoinedAt';
import { noop } from 'functions/noop';
import { setTokens, setSession } from 'components/Authentication/functions';
import { authenticationApi } from 'components/Authentication/api';
import { authenticationActions, setUserData, userAuthenticationSuccess } from 'components/Authentication/actions';
import { conditionsChecker, getQuestionnaireQuestions } from 'components/Questionnaire/functions';
import { render } from 'react-dom';
import { GENERAL_DISEASE } from 'utils/constants/disease';
import {
  TREATMENT_QUESTION_ID_BY_DISEASE,
  SUBTYPE_ALIAS_QUESTION_IDS_BY_DISEASE,
  QUESTION_IDS_BY_DISEASE,
} from 'modules/questionnaire/constants';
import { MATCH_FLOWS } from 'modules/matches/constants';
import { MatchFlow } from 'modules/matches/matchFlow';
import { getUserWixId } from 'modules/user/utils';
import { PageHeaderView } from 'components/PageHeader/PageHeaderView';
import { TAB_IDS } from 'components/PageHeader/constants';
import { MatchesSkeleton } from 'new-ui/MatchesList/MatchesSkeleton';
import { MatchesListHeaderView } from 'new-ui/MatchesList/MatchesListHeader/MatchesListHeaderView';
import app from './app';
import { conditionProfileFromIndication } from './functions/conditionProfileFromCondition';
import Overlay from './Components/Overlay';

const fakeStore = createStore((state) => state);

export const isMobileWidth = () => document.body.clientWidth <= MOBILE_WIDTH;

export const capitalizeFirstLetter = (o) => {
  if (typeof o === 'string') return o.charAt(0).toUpperCase() + o.slice(1);
  return o;
};

export const getDrugName = (code) => {
  if (!app?.drugs?.length) return code;
  const drug = app.drugs.find((a) => a.code === code);
  return drug ? drug.name : code;
};

export const DOM = (name, props = {}, root) => {
  const tag = name.match(/^[a-zA-Z0-9]+/);
  const o = props;
  const el = document.createElement(tag ? tag[0] : 'div');
  let attr;
  name.split(/(?=\.)|(?=#)|(?=\[)/).forEach((c) => {
    c[0] === '#' && (el.id = c.substr(1));
    c[0] === '.' && (el.classList.add(c.substr(1)));
    c[0] === '[' && (attr = c.substr(1, c.length - 2).split('='), el.setAttribute(attr[0], attr[1]));
  });
  root && (el.root = root, root[name] = el);
  for (const p in o.style) el.style[p] = o.style[p];
  delete o.style;
  if (o.name && root) root[`$${o.name}`] = el;
  for (const x in o) x[0] === '/' ? el.appendChild(DOM(x.substr(1), o[x], root || el)) : el[x] = o[x];
  return el;
};

export const getMissingCriticalDataKeys = (user = {}) => {
  if (!user?.missing_critical_info) return [];
  return Object.keys(user.missing_critical_info).reduce((a, b) => {
    if (user.missing_critical_info[b]) a.push(b);
    return a;
  }, []);
};

const getMissingCriticalFromTrials = (trials = []) => trials.reduce((a, b) => {
  if (b.missed_questions && b.missed_questions.length) {
    b.missed_questions.forEach((mq) => {
      if (a.indexOf(mq) === -1) a.push(mq);
    });
  }
  return a;
}, []);

export const getMissingCriticalQuestions = (user = {}, trials = []) => {
  let missingCriticalQuestions = [];
  try {
    missingCriticalQuestions = uniq(getMissingCriticalDataKeys(user).concat(getMissingCriticalFromTrials(trials)));
  } catch (e) {
    console.error('error collecting missing critical', e);
  }
  return missingCriticalQuestions;
};

export const getTopGroups = (trials = [], num = 3) => Object.fromEntries(Object.entries(trials.reduce((a, b) => {
  b.groups.forEach((g) => {
    if (!a[g]) a[g] = 1;
    else a[g]++;
  });
  return a;
}, {})).sort((a, b) => b[1] - a[1]).slice(0, num));

// eslint-disable-next-line max-len
export const randomString = () => Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);

export const sendEmail = (recipient = '', subject = '', body = '') => {
  const mailtoUrl = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = mailtoUrl;
};

export const isMobile = () => {
  try {
    document.createEvent('TouchEvent');
    return true;
  } catch (e) {
    return false;
  }
};

export const isCrmChatAllowed = () => {
  if (!getUserWixId(app.user)) {
    return false;
  }
  const hasSponsored = (app.trials || []).filter((t) => t.isSponsored).length > 0;
  const condition = app.user.personal?.condition;
  const availableDiseases = app?.coniguration?.crmChatDiseases || [];

  return availableDiseases.includes(condition) && hasSponsored;
};

export const isCallUsMode = () => (
  (app.trials || []).filter((t) => t.isSponsored).length > 0
);

export const getIndicationDetailsTitle = (indication = app.user?.personal.condition, subDisease = app.user?.condition_profile?.type_of_ca) => {
  if (indication === GENERAL_DISEASE) {
    return window.t(`quest_selector.cancer_types.${indication}.${subDisease}`);
  }
  return window.t(`quest_selector.cancer_types.${indication}`);
};

export const getIndicationIntercomTitle = (indication = app.user?.personal.condition, subDisease = app.user?.condition_profile?.type_of_ca) => {
  if (indication === GENERAL_DISEASE) {
    return window.t(`quest_selector.cancer_types.${indication}.${subDisease}`);
  }
  return window.t(`intercom.condition.title.${indication}`);
};

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    return false;
  }
};

export const hasGenderQuestion = () => !!app.questions?.find((a) => a.id === 'gender');

// partly duplicate logic of requestSignup from components/Authentication/actions
// TODO move to components/Authentication/actions
const setUser = async (
  {
    accessToken, refreshToken, user, personal,
  },
  justSignedUp,
) => {
  setTokens(accessToken, refreshToken);
  localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user));

  setSession(user);
  if (personal) {
    user.personal = personal;
  }
  setJoinedAt();

  const profile = {
    info: {
      user_wix_id: user.user_wix_id,
      email: user.email,
      family_name: user.family_name || null,
      phone: user.phone || null,
      user_type: user.user_type || null,
      country: user.country || null,
    },
    personal: {
      user_wix_id: user.user_wix_id,
    },
    condition_profile: {
      user_type: user?.user_type || null,
    },
  };

  app.store.dispatch(setUserData(profile));
  app.store.dispatch(userAuthenticationSuccess(user, justSignedUp));

  return user;
};

export const registerUser = async ({
  email,
  indication,
  recaptchaToken,
  password = `E${randomString()}!`,
  info = {},
  condition_profile = {},
  personal = {},
  statistic = {},
}) => {
  const tempUserData = {
    info: {
      ...info ?? {},
    },
    personal: {
      condition: indication,
      ...personal ?? {},
    },
    condition_profile,
  };

  const tempUser = await authenticationApi.createTempUser(tempUserData);

  tempUser.personal.is_internal = 0;

  const signupData = {
    personal: {
      ...tempUser.personal,
      providerCode: tempUserData.personal.providerCode,
    },
    condition_profile: {
      ...tempUser.condition_profile,
      dob: tempUserData.dob,
    },
    info: {
      user_wix_id: null,
      email,
      password,
      country: null,
      family_name: null,
      phone: '',
      partner: null,
      user_type: null,
      sign_up_source: window.location.pathname.includes('ai_profile') ? 'file_upload' : 'manual',
      ...info,
    },
    statistic,
    recaptchaToken,
  };

  const result = await authenticationApi.signup(signupData);

  let success = true;
  if (!result.data.access_token || !result.data.refresh_token || !result.data.user) {
    console.error('Wrong response from the profile server');
    success = false;
  }

  const prepareData = () => {
    const { data: { user: userString, access_token: accessToken, refresh_token: refreshToken } } = result;

    const user = JSON.parse(userString);
    return {
      accessToken,
      refreshToken,
      user: {
        ...user,
        // user_type does not come from the server in signup response
        user_type: user.user_type ?? signupData.info.user_type,
      },
    };
  };

  return { success, data: prepareData() };
};

/**
 * signup
 * minimal requirement are: email, indication, recaptchToken
 * @param {*} data object
 */
export const signup = async ({
  onsignup,
  onerror,
  ...registrationPayload
}) => {
  try {
    const { success, data: registrationResult } = await registerUser(registrationPayload);

    if (!success) {
      // response can contain helpful message
      return registrationResult;
    }

    MatchFlow.setMatchFlow(MATCH_FLOWS.BASIC);

    await setUser(registrationResult, true);

    const user = await app.store.dispatch(authenticationActions.getUser());

    if (onsignup) onsignup(user);
  } catch (e) {
    if (onerror) onerror(e);
  }
};

export const getBiomarkerTitle = (biomarker, all_biomarkers = app.biomarkers || [], t = window.t) => {
  const f = all_biomarkers.filter((a) => (a.code === biomarker));
  return (f && f.length) ? t(f[0].name) : biomarker;
};

export const filterQuestionsByCondition = async ({
  questionsIds = [],
  conditionProfile,
  condition,
}) => {
  const allQuestions = await getQuestionnaireQuestions(condition);
  const filteredQuestions = questionsIds.filter((questionId) => {
    const questionCondition = allQuestions.find((q) => q.id === questionId)?.conditions;
    return conditionsChecker(
      conditionProfile,
      questionCondition,
    );
  });
  return filteredQuestions;
};

export const getInitialConditionProfileByIndication = async (indication, condition_profile = {}) => {
  if (Array.isArray(condition_profile?.biomarkers)) {
    condition_profile.biomarkers = condition_profile.biomarkers.filter((a) => a !== 'none');
  }

  const conditionProfile = await conditionProfileFromIndication(indication);
  for (const attr in condition_profile) {
    if (attr !== 'qstack') conditionProfile[attr] = condition_profile[attr];
  }
  if (condition_profile.qstack) {
    condition_profile.qstack.forEach((q) => {
      if (conditionProfile.qstack.indexOf(q === -1)) conditionProfile.qstack.push(q);
    });
  }
  return conditionProfile;
};

export const createEmptyUser = async (indication, _condition_profile = {}, _info = {}, _personal = {}) => {
  const conditionProfile = await getInitialConditionProfileByIndication(indication);
  conditionProfile.qstack.forEach((prop) => {
    if (conditionProfile[prop] === undefined) conditionProfile[prop] = null;
  });

  const condition_profile = {
    ...conditionProfile,
    ..._condition_profile,
  };

  const info = {
    condition: indication,
    country: null,
    email: null,
    family_name: null,
    partner: null,
    phone: null,
    user_type: null,
    user_wix_id: null,
    ..._info,
  };

  const personal = {
    patient_id: null,
    user_wix_id: null,
    profile_id: null,
    condition: indication,
    dob: null,
    gender: null,
    ..._personal,
  };

  const o = {
    condition_profile,
    info,
    personal,
  };

  return o;
};

export function isCurrentTimeInESTWorkingRange() {
  // Tmp remove time limitation
  return true;
  // const now = new Date();
  // const utc = now.getTime() + now.getTimezoneOffset() * 60000; // Convert to UTC
  // const estOffset = -5 * 60; // Eastern Standard Time (New York) offset in minutes
  // const estTime = new Date(utc + 60000 * estOffset); // Convert to EST

  // const startHour = 8;
  // const endHour = 17;
  // const currentHour = estTime.getHours();
  // const currentMinutes = estTime.getMinutes();
  // const dayOfWeek = estTime.getDay();

  // return (
  //   (currentHour > startHour || (currentHour === startHour && currentMinutes >= 30))
  //   && (currentHour < endHour || (currentHour === endHour && currentMinutes <= 30))
  //   && (dayOfWeek !== 0 && dayOfWeek !== 6)
  // );
}

/**
 * callOnceEvery
 * @description callback function can be called only once every ms, calling this function more than once during that period will be ignored
 * for instance if you want a certain popup to be called only once every 7 days (()=>{}, 'key_name', 6.048e+8)
 * @param {*} callback function to call
 * @param {*} key special key (string no spaces)
 * @param {*} ms milliseconds time
 */
export const callOnceEvery = (callback, key = '', ms = 0) => {
  const storageKey = `call_every_${key}`;
  const time = localStorage.getItem(storageKey);
  const callAndCreate = () => {
    callback();
    const nextCallTime = new Date(new Date().getTime() + ms);
    localStorage.setItem(storageKey, nextCallTime.toISOString());
  };
  if (time) {
    const dueDate = new Date(time);
    const now = new Date();
    if (now <= dueDate) return;
  }
  callAndCreate();
};

export const debounce = (func, timeout = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};

export const getOverlay = () => {
  const $overlay = DOM('.generic-overlay', {
    '/.generic-overlay-content': {
      name: 'content',
    },
  });
  $overlay.add = () => {
    document.body.appendChild($overlay);
  };
  $overlay.show = () => {
    $overlay.classList.add('generic-overlay-visible');
  };
  $overlay.hide = () => {
    $overlay.classList.remove('generic-overlay-visible');
  };
  $overlay.append = ($dom) => {
    $overlay.$content.appendChild($dom);
  };
  $overlay.remove = () => {
    if ($overlay.parentElement) $overlay.parentElement.removeChild($overlay);
  };
  return $overlay;
};

export const waitFor = (tester, onsuccess, ontimeout = () => {
}, maxTimeout = 10000, ms = 50) => {
  if (tester()) return onsuccess();
  let timer;
  // eslint-disable-next-line prefer-const
  timer = setInterval(() => {
    if (tester()) {
      clearInterval(timer);
      onsuccess();
    }
  }, ms);
  setTimeout(() => {
    clearInterval(timer);
    ontimeout();
  }, maxTimeout);
};

export const questionsNormalizer = (questionsArray = [], condition) => {
  const fieldsByIndication = QUESTION_IDS_BY_DISEASE;
  let qs = questionsArray.map((q) => {
    if (q === 'type') return fieldsByIndication[condition]?.diseaseType;
    if (q === 'disease_stage') return fieldsByIndication[condition]?.patientStage;
    if (q === 'received_treatment') return TREATMENT_QUESTION_ID_BY_DISEASE[condition];
    return q;
  }).filter((a) => a);
  if (questionsArray.includes('subtype') && SUBTYPE_ALIAS_QUESTION_IDS_BY_DISEASE[condition]) {
    qs = qs.concat(SUBTYPE_ALIAS_QUESTION_IDS_BY_DISEASE[condition]);
  }
  return qs;
};

export const isEmptyObject = (o) => {
  if (o === undefined || o === null || JSON.stringify(o) === '{}') return true;
  return false;
};

export const displayDashboardSkeleton = () => {
  const $o = DOM('.dashboard-skeleton-holder');
  document.body.appendChild($o);
  render(
    <Overlay hideTop className="dashboard-skeleton-overlay">
      <div className="new-ui">
        <Provider store={fakeStore}>
          <PageHeaderView
            isLogoShown
            isNavMenuShown
            isProviderMode={!!app.coniguration?.providerSpecific?.code}
            tabIds={[TAB_IDS.TREATMENT_OPTIONS, TAB_IDS.SUPPORT_PROGRAMS, TAB_IDS.SPEAK_WITH_TINA]}
            onMobileMenuItemClick={noop}
          />
          <main>
            <div className="results">
              <div className="grid">
                <div className="widget">
                  <MatchesListHeaderView />
                  <div className="dashboard-skeleton-results">
                    <MatchesSkeleton />
                  </div>
                </div>
              </div>
            </div>
          </main>
        </Provider>
      </div>
    </Overlay>,
    $o,
  );
};

window.displayDashboardSkeleton = displayDashboardSkeleton;

export const removeDashboardSkeleton = () => {
  const skeletonHolder = document.querySelector('.dashboard-skeleton-overlay');
  if (skeletonHolder?.parentElement) {
    skeletonHolder.parentElement.removeChild(skeletonHolder);
  }
};

window.removeDashboardSkeleton = removeDashboardSkeleton;

export const isTreatmentTrialDisabled = () => app.isSOCDisabled();

export const hasUserSponsoredTrials = () => app.trials?.some((trial) => trial?.isSponsored);
